import {Component, OnInit,} from "@angular/core";
import { Router } from "@angular/router";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { PurchaseType } from "@app/api-client/v2/wallet.service";

import { ProgramService } from "@app/api-client/v2/program.service";
import { EbookService } from "@app/api-client/v2/ebook.service";
import { CatalogV2Service } from "@app/api-client/v2/catalog-v2.service";
import { IEbookViewModel } from "@app/api-client/models/v2/i-ebook-view-model";
import { IProgram } from "@interfaces/i-program";
import { IVVideo } from "@interfaces/i-v-video";


import { VideoV3Service } from "@services/video-v3.service";
import { Cardlist, IResponseMinimalCard, PaymentContent } from "@interfaces/i-response-card-view-model";
import { validateExpiredCard } from "@app/shared/helpers/validate-expired-card.helper";
import { HomeCatalogStateService } from "@app/shared/services/home-catalog-state.service";
import { PaymentSuccessComponent } from "@app/shared/components/modals/payment-success/payment-success.component";
import { PaymentErrorComponent } from "@app/shared/components/modals/payment-error/payment-error.component";
import { MarketingParamsService } from "@app/shared/utils/marketing-params.service";
import { ContentDetail } from "@app/core/models/interfaces/content-detail";
import { LoadingOverlayService } from "@app/shared/services/loading-overlay.service";
import {
  IPreviewTaxEstimationResponse,
  ITaxTransactionRequest,
  TaxEstimationValue,
} from "@app/api-client/models/v2/i-tax-transaction-request";
import { WalletV3Service } from "@app/shared/services/wallet-v3.service";
import { finalize } from "rxjs/operators";

@Component({
  selector: "checkout-modal-app",
  templateUrl: "./checkout-modal.component.html",
  styleUrls: ["./checkout-modal.component.scss"],
})
export class CheckoutModalComponent implements OnInit {

  contentDetail: ContentDetail;
  paymentContent: PaymentContent;

  cards: IResponseMinimalCard[] = [];
  cardsList: Cardlist[] = [];
  imageUrl: string;
  selectedCardId: string | null = null;
  countryCode: string;
  previewTax: TaxEstimationValue;
  defaultImage: 'assets/img/no-image-new.webp';
  titleContent: string = "";
  private isCardChange = false;

  constructor(
    public activeModal: NgbActiveModal,
    private router: Router,
    private videoService: VideoV3Service,
    private programService: ProgramService,
    private ebookService: EbookService,
    private catalogService: CatalogV2Service,
    private modalService: NgbModal,
    private walletV3Service: WalletV3Service,
    private catalogState: HomeCatalogStateService,
    private marketingParamsService: MarketingParamsService,
    private loadingOverlayService: LoadingOverlayService
  ) {}

  ngOnInit() {
    this.marketingParamsService.sendMarketingTrack({
      event_location: 'Checkout',
      event_action: this.paymentContent.isRent ? "rent" : "purchase",
    });
    this.getUserCreditCards();
  }

  private getUserCreditCards(): void {
    this.loadingOverlayService.showLoadingOverlay();
    this.walletV3Service.getUserCreditCardsMinimal()
      .subscribe((creditCards) => {
        if (creditCards.length <= 0) {
          this.redirectTo();
          return;
        }

        this.cards = creditCards.map(validateExpiredCard).filter(item => !item.expired)
        if (!this.cards.length) {
          this.redirectTo();
          return;
        }

        this.cardsList = this.cards.map(card => {
          return {
            textCard: `${card.cardBrandName} que termina en ${card.last4}`,
            date: `${card.expMonth}/${card.expYear}`,
            cardId: card.cardId,
            textCardPlane: `${card.cardBrandName} -  ${card.last4}`,
            isDefault: card.isDefault,
            countryCode: card.countryCode,
          }
        });

        const defaultCard = this.cardsList.find(c => c.isDefault);
        if (defaultCard) {
          this.selectedCardId = defaultCard.cardId;
          this.countryCode = defaultCard.countryCode
        }

        this.previewTaxCalculation();
        this.loadingOverlayService.hideLoadingOverlay();
      });
  }

  previewTaxCalculation(): void {
    if (this.isCardChange) {
      this.loadingOverlayService.showLoadingOverlay();
    }
    const payloadPreviewTax: ITaxTransactionRequest = {
      amount: this.paymentContent.amount,
      countryCode: this.countryCode
    }
    this.walletV3Service.previewTaxEstimation(payloadPreviewTax)
      .subscribe((data: IPreviewTaxEstimationResponse) => {
        this.previewTax = data.value;
        if (this.isCardChange) {
          this.loadingOverlayService.hideLoadingOverlay();
        }
      });
  }

  selectCard(card: Cardlist) {
    if (this.selectedCardId === card.cardId) return;
    this.isCardChange = true;
    this.selectedCardId = card.cardId;
    this.countryCode = card.countryCode
    this.previewTaxCalculation();
  }

  pay(): void {
    this.loadingOverlayService.showLoadingOverlay();
    const payloadPay = {
      contentId: this.paymentContent.contentId,
      contentType: this.paymentContent.contentType,
      cardId: this.selectedCardId,
      acquisitionType: this.paymentContent.isRent
        ? PurchaseType.Rent
        : PurchaseType.Purchase,
      subTotal: this.paymentContent.amount,
      taxes: this.previewTax.totalTax,
      saveCard: false,
      description: this.buildPayDescription(
        this.paymentContent.isRent ? PurchaseType.Rent : PurchaseType.Purchase,
      ),
    };

    this.walletV3Service
      .payWithCreditCard(payloadPay)
      .pipe(finalize(() => this.loadingOverlayService.hideLoadingOverlay()))
      .subscribe({
        next: (response) => {
          if (response?.success) {
            this.updatePPVContentInfoCatalogCache(
              this.paymentContent.contentId,
              this.paymentContent.contentType,
            );
            this.openModalSuccess();
            this.closeModal();
            this.trackSendMarketing("PPV_success");
          } else {
            this.openModalError();
            this.trackSendMarketing("PPV_error");
          }
        },
        error: (err) => {
          this.openModalError();
          this.trackSendMarketing("PPV_error");
        },
      });
  }

  updatePPVContentInfoCatalogCache(
    contentId: string,
    contentType: string,
    option: boolean = true
  ): void {
    if (contentType == "video") {
      this.videoService.getView(contentId).subscribe((video: IVVideo) => {
        //Update catalog cache
        if (option) {
          this.catalogService.updatePPVContentInfo(video);
          this.catalogState.clearCatalog();
        } else {
          this.titleContent = video.title;
          this.paymentContent.imageContent =
            video.backgroundImageUrl || video.thumbnailUrl;
        }
      });
    }
    if (contentType == "program") {
      this.programService
        .getView({ programId: contentId })
        .then((program: IProgram) => {
          //Update catalog cache
          if (option) {
            this.catalogService.updatePPVContentInfo(program);
            this.catalogState.clearCatalog();
          } else {
            this.titleContent = program.title;
            this.paymentContent.imageContent =
              program.urlBackgroundImage || program.urlImage;
          }
        });
    }
    if (contentType == "ebook") {
      this.ebookService
        .view({ ebookId: contentId, puserId: null })
        .then((ebook: IEbookViewModel) => {
          //Update catalog cache
          if (option) {
            this.catalogService.updatePPVContentInfo(ebook);
            this.catalogState.clearCatalog();
          } else {
            this.titleContent = ebook.title;
            this.paymentContent.imageContent = ebook.urlBackgroundImage;
          }
        });
    }
  }

  private navigateToAcquire(extraQueryParams: any = {}, closeModals: boolean = false) {
    const baseQueryParams = {
      action: this.paymentContent.isRent
        ? PurchaseType.Rent
        : PurchaseType.Purchase,
      ...extraQueryParams
    };

    this.router.navigate(
      ["/acquire", this.paymentContent.contentType, this.paymentContent.contentId],
      { queryParams: baseQueryParams }
    );

    if (closeModals) {
      this.closeModal();
    }
  }

  redirectTo(): void {
    this.navigateToAcquire();
  }

  goToOtherPayment(): void {
    const queryParams = this.marketingParamsService.getQueryParamsMarketing();

    this.marketingParamsService.sendMarketingTrack({
      event_location: "other_cc",
      event_action: this.paymentContent.isRent ? "rent" : "purchase",
    });

    this.navigateToAcquire(queryParams, true);
  }

  buildPayDescription(action: PurchaseType) {
    let description =
      (action == PurchaseType.Purchase ? "Compra" : "Alquiler") +
      " " +
      "Contenido PPV | Enlace+";
    description += "\n";
    switch (this.paymentContent.contentType) {
      case "video":
        description +=
          "Contenido adquirido: Video | " + " " + this.paymentContent.title;
        break;
      case "program":
        description +=
          "Contenido adquirido: Programa |" + " " + this.paymentContent.title;
        break;
      case "ebook":
        description +=
          "Contenido adquirido: Libro Digital" +
          " " +
          this.paymentContent.title;
        break;
    }
    return description;
  }

  onImageError(event: Event) {
    const element = event.target as HTMLImageElement;
    element.src = 'assets/img/no-image-new.webp';
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  openModalSuccess(): void {
    const modalRef = this.modalService.open(PaymentSuccessComponent, {
      windowClass: "dark-modal modal-payment",
      backdropClass: "light-black-backdrop",
    });

    modalRef.componentInstance.contentDetail = this.contentDetail;
    modalRef.componentInstance.action = this.paymentContent.isRent
      ? "rent"
      : "purchase";
    modalRef.componentInstance.contentType = this.paymentContent.contentType;
    modalRef.componentInstance.fromMobile = false;
  }

  openModalError(): void {
    const modalRef = this.modalService.open(PaymentErrorComponent, {
      windowClass: "dark-modal modal-payment",
      backdropClass: "light-black-backdrop",
    });
    modalRef.componentInstance.action = this.paymentContent.isRent
      ? "rent"
      : "purchase";
  }

  trackSendMarketing(event_status: string) {
    this.marketingParamsService.sendMarketingTrack({
      event_location: "Checkout",
      event_status: event_status,
    });
  }
}
